import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { resourcesAPI, metricsAPI, binderAPI, claimsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    ArrowLeft, 
    MapPin, 
    Globe, 
    Phone, 
    Mail, 
    User,
    Building2,
    ExternalLink,
    Facebook,
    Linkedin,
    Users,
    Calendar,
    TrendingUp,
    Award,
    Briefcase,
    Pencil,
    History,
    CheckCircle,
    Clock,
    Bookmark,
    FolderOpen,
    Loader2,
    Plus,
    ShieldCheck,
    Send
} from 'lucide-react';

const ResourceDetailPage = () => {
    const { id } = useParams();
    const { user, isAuthenticated } = useAuth();
    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [annualMetrics, setAnnualMetrics] = useState([]);
    
    // Save to binder state
    const [showSaveToBinder, setShowSaveToBinder] = useState(false);
    const [binders, setBinders] = useState([]);
    const [selectedBinder, setSelectedBinder] = useState('');
    const [saveNotes, setSaveNotes] = useState('');
    const [savingToBinder, setSavingToBinder] = useState(false);
    const [loadingBinders, setLoadingBinders] = useState(false);
    
    // Claim organization state
    const [showClaimDialog, setShowClaimDialog] = useState(false);
    const [claimStatus, setClaimStatus] = useState(null); // { claim, has_access }
    const [claimLoading, setClaimLoading] = useState(false);
    const [claimForm, setClaimForm] = useState({ role_at_org: '', justification: '', contact_email: '' });

    // Check if current user can edit this resource
    const canEdit = resource && user && (
        user.is_admin ||
        resource.created_by === user.user_id ||
        resource.can_edit?.includes(user.user_id)
    );

    useEffect(() => {
        const fetchResource = async () => {
            try {
                const response = await resourcesAPI.getById(id);
                setResource(response.data);
            } catch (err) {
                setError('Resource not found');
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [id]);

    // Fetch annual metrics if user is authenticated
    useEffect(() => {
        if (isAuthenticated && id) {
            metricsAPI.getOrgMetrics(id)
                .then(res => setAnnualMetrics(res.data.metrics || []))
                .catch(() => setAnnualMetrics([]));
        }
    }, [isAuthenticated, id]);

    // Fetch claim status
    useEffect(() => {
        if (isAuthenticated && id) {
            claimsAPI.getClaimStatus(id)
                .then(res => setClaimStatus(res.data))
                .catch(() => setClaimStatus(null));
        }
    }, [isAuthenticated, id]);

    const openSaveToBinder = async () => {
        setShowSaveToBinder(true);
        setSelectedBinder('');
        setSaveNotes('');
        if (binders.length === 0) {
            setLoadingBinders(true);
            try {
                const res = await binderAPI.getBinders();
                setBinders(res.data || []);
            } catch {
                setBinders([]);
            } finally {
                setLoadingBinders(false);
            }
        }
    };

    const handleSaveToBinder = async () => {
        if (!selectedBinder) {
            toast.error('Please select a binder');
            return;
        }
        setSavingToBinder(true);
        try {
            await binderAPI.saveResource(selectedBinder, {
                resource_id: id,
                notes: saveNotes || undefined
            });
            toast.success('Resource saved to binder!');
            setShowSaveToBinder(false);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to save resource';
            if (error.response?.status === 409) {
                toast.info('This resource is already in that binder');
            } else {
                toast.error(msg);
            }
        } finally {
            setSavingToBinder(false);
        }
    };

    const handleSubmitClaim = async () => {
        if (!claimForm.role_at_org.trim()) {
            toast.error('Please enter your role at the organization');
            return;
        }
        setClaimLoading(true);
        try {
            await claimsAPI.submitClaim(id, {
                role_at_org: claimForm.role_at_org,
                justification: claimForm.justification,
                contact_email: claimForm.contact_email || user.email
            });
            toast.success('Claim submitted! An admin will review your request.');
            setShowClaimDialog(false);
            setClaimStatus({ claim: { status: 'pending' }, has_access: false });
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to submit claim';
            toast.error(msg);
        } finally {
            setClaimLoading(false);
        }
    };

    // Type color mapping - rainbow-inspired
    const typeColors = {
        'Employer': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
        'Training Provider': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
        'Service Provider': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'Support Organization': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'Government': 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
        'Foundation or Private Funder': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        'Higher Education': 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
        'Parents & Caregivers': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
        'Workforce Alliance': 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
        'High School': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
        'Transportation': 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background" data-testid="resource-detail-loading">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <Skeleton className="h-8 w-32 mb-8" />
                    <Skeleton className="h-12 w-3/4 mb-4" />
                    <Skeleton className="h-6 w-1/2 mb-8" />
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            <Skeleton className="h-48 w-full" />
                        </div>
                        <div>
                            <Skeleton className="h-48 w-full" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !resource) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center" data-testid="resource-detail-error">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-bold mb-4">Resource Not Found</h2>
                    <p className="text-muted-foreground mb-6">
                        The resource you're looking for doesn't exist or has been removed.
                    </p>
                    <Link to="/directory">
                        <Button>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Public Directory
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="resource-detail-page">
            {/* Back Navigation */}
            <div className="bg-card border-b border-border">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link 
                        to="/directory" 
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="back-to-directory"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Directory
                    </Link>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {resource.can_edit?.length > 0 && (
                            <Badge className="bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 gap-1" data-testid="haape-member-badge">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                HAAPE Member
                            </Badge>
                        )}
                        {resource.is_featured && (
                            <Badge className="bg-primary">Featured</Badge>
                        )}
                        {resource.organization_types?.map((type, index) => (
                            <Badge 
                                key={index}
                                variant="secondary"
                                className={typeColors[type] || 'bg-muted text-muted-foreground'}
                            >
                                {type}
                            </Badge>
                        ))}
                    </div>
                    
                    <div className="flex items-start gap-6">
                        {resource.organization_pic && (
                            <img 
                                src={resource.organization_pic} 
                                alt={resource.name}
                                className="w-24 h-24 object-cover rounded-lg border border-border shrink-0"
                            />
                        )}
                        <div className="flex-1">
                            <div className="flex items-start justify-between gap-4">
                                <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3" data-testid="resource-name">
                                    {resource.name}
                                </h1>
                                {canEdit && (
                                    <Link to={`/organizations/${resource.resource_id}/edit`}>
                                        <Button variant="outline" size="sm" className="gap-2">
                                            <Pencil className="h-4 w-4" />
                                            Edit
                                        </Button>
                                    </Link>
                                )}
                                {isAuthenticated && (
                                    <Button variant="outline" size="sm" className="gap-2" onClick={openSaveToBinder} data-testid="save-to-binder-btn">
                                        <Bookmark className="h-4 w-4" />
                                        Save to Binder
                                    </Button>
                                )}
                                {isAuthenticated && !canEdit && claimStatus && !claimStatus.has_access && (
                                    claimStatus.claim?.status === 'pending' ? (
                                        <Badge variant="secondary" className="gap-1 py-1.5 px-3" data-testid="claim-pending-badge">
                                            <Clock className="h-3.5 w-3.5" />
                                            Claim Pending
                                        </Badge>
                                    ) : claimStatus.claim?.status === 'approved' ? null : (
                                        <Button variant="outline" size="sm" className="gap-2" onClick={() => {
                                            setClaimForm({ role_at_org: '', justification: '', contact_email: user?.email || '' });
                                            setShowClaimDialog(true);
                                        }} data-testid="claim-org-btn">
                                            <ShieldCheck className="h-4 w-4" />
                                            Claim Organization
                                        </Button>
                                    )
                                )}
                            </div>
                            
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="h-4 w-4" />
                                <span>
                                    {resource.city ? `${resource.city}, ` : ''}{resource.state}
                                </span>
                            </div>
                            {resource.address && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    {resource.address}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Impact Metrics Banner - only for CLAIMED orgs, from latest approved annual metrics */}
                {resource.can_edit?.length > 0 && (() => {
                    const approved = annualMetrics.filter(m => m.status === 'approved');
                    if (approved.length === 0) return null;
                    const latest = approved.reduce((a, b) => (a.year > b.year ? a : b));
                    const hasData = latest.people_trained_current_year || latest.people_employed_current_year || 
                                    latest.completion_rate || latest.placement_rate || 
                                    latest.employers_engaged || latest.avg_starting_salary;
                    if (!hasData) return null;
                    return (
                        <Card className="mb-8 bg-primary/5 border-primary/20" data-testid="impact-metrics-banner">
                            <CardContent className="py-6">
                                <h3 className="font-semibold text-sm uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Impact Metrics ({latest.year})
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                    {latest.people_trained_current_year != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{latest.people_trained_current_year.toLocaleString()}</p>
                                            <p className="text-xs text-muted-foreground">Trained This Year</p>
                                        </div>
                                    )}
                                    {latest.people_employed_current_year != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{latest.people_employed_current_year.toLocaleString()}</p>
                                            <p className="text-xs text-muted-foreground">Employed This Year</p>
                                        </div>
                                    )}
                                    {latest.completion_rate != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{latest.completion_rate}%</p>
                                            <p className="text-xs text-muted-foreground">Completion Rate</p>
                                        </div>
                                    )}
                                    {latest.placement_rate != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{latest.placement_rate}%</p>
                                            <p className="text-xs text-muted-foreground">Placement Rate</p>
                                        </div>
                                    )}
                                    {latest.employers_engaged != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{latest.employers_engaged}</p>
                                            <p className="text-xs text-muted-foreground">Employers Engaged</p>
                                        </div>
                                    )}
                                    {latest.avg_starting_salary != null && (
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">${latest.avg_starting_salary.toLocaleString()}</p>
                                            <p className="text-xs text-muted-foreground">Avg Starting Salary</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })()}

                <div className="grid md:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="md:col-span-2 space-y-6">
                        {/* Description */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-primary" />
                                    About
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground leading-relaxed">
                                    {resource.description}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Population Served */}
                        {resource.nd_population_served && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Users className="h-5 w-5 text-primary" />
                                        Population Served
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground">
                                        {resource.nd_population_served}
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Services Offered */}
                        {resource.services_offered?.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Services Offered</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        {resource.services_offered.map((service, index) => (
                                            <Badge key={index} variant="outline">
                                                {service}
                                            </Badge>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Annual Metrics History - only for claimed orgs */}
                        {resource.can_edit?.length > 0 && annualMetrics.length > 0 && (
                            <Card data-testid="annual-metrics-history">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <History className="h-5 w-5 text-primary" />
                                        Annual Impact History
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {annualMetrics.filter(m => m.status === 'approved').map((m) => (
                                            <div key={m.metrics_id} className="p-3 border border-border rounded-lg" data-testid={`annual-metric-${m.year}`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="font-mono font-bold text-lg text-primary">{m.year}</span>
                                                    <Badge variant="default" className="text-xs">
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                        Verified
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                                    {m.people_trained_current_year != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Trained</p>
                                                            <p className="font-semibold">{m.people_trained_current_year}</p>
                                                        </div>
                                                    )}
                                                    {m.people_employed_current_year != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><Briefcase className="h-3 w-3" /> Employed</p>
                                                            <p className="font-semibold">{m.people_employed_current_year}</p>
                                                        </div>
                                                    )}
                                                    {m.completion_rate != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><Award className="h-3 w-3" /> Completion</p>
                                                            <p className="font-semibold">{m.completion_rate}%</p>
                                                        </div>
                                                    )}
                                                    {m.placement_rate != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Placement</p>
                                                            <p className="font-semibold">{m.placement_rate}%</p>
                                                        </div>
                                                    )}
                                                    {m.employers_engaged != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><Building2 className="h-3 w-3" /> Employers</p>
                                                            <p className="font-semibold">{m.employers_engaged}</p>
                                                        </div>
                                                    )}
                                                    {m.avg_starting_salary != null && (
                                                        <div>
                                                            <p className="text-muted-foreground text-xs flex items-center gap-1"><Award className="h-3 w-3" /> Avg Salary</p>
                                                            <p className="font-semibold">${m.avg_starting_salary.toLocaleString()}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Contact Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Contact Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {resource.website && (
                                    <a 
                                        href={resource.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                                        data-testid="resource-website"
                                    >
                                        <Globe className="h-4 w-4 text-muted-foreground" />
                                        <span className="truncate">Visit Website</span>
                                        <ExternalLink className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                                
                                {resource.contact_name && (
                                    <div className="flex items-center gap-3 text-sm">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        <span>{resource.contact_name}</span>
                                    </div>
                                )}
                                
                                {(resource.contact_email || resource.general_email) && (
                                    <a 
                                        href={`mailto:${resource.contact_email || resource.general_email}`}
                                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                                    >
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        <span className="truncate">
                                            {resource.contact_email || resource.general_email}
                                        </span>
                                    </a>
                                )}
                                
                                {resource.phone_number && (
                                    <a 
                                        href={`tel:${resource.phone_number}`}
                                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                                    >
                                        <Phone className="h-4 w-4 text-muted-foreground" />
                                        <span>{resource.phone_number}</span>
                                    </a>
                                )}

                                {(resource.facebook_url || resource.linkedin_url) && (
                                    <>
                                        <Separator />
                                        <div className="flex gap-2">
                                            {resource.facebook_url && (
                                                <a 
                                                    href={resource.facebook_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-md bg-muted hover:bg-muted/80 transition-colors"
                                                >
                                                    <Facebook className="h-4 w-4" />
                                                </a>
                                            )}
                                            {resource.linkedin_url && (
                                                <a 
                                                    href={resource.linkedin_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 rounded-md bg-muted hover:bg-muted/80 transition-colors"
                                                >
                                                    <Linkedin className="h-4 w-4" />
                                                </a>
                                            )}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Actions */}
                        {resource.website && (
                            <a 
                                href={resource.website}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button className="w-full gap-2" data-testid="visit-website-btn">
                                    <Globe className="h-4 w-4" />
                                    Visit Website
                                    <ExternalLink className="h-3 w-3" />
                                </Button>
                            </a>
                        )}

                        {/* Metadata */}
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            <span>
                                Added {new Date(resource.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Save to Binder Dialog */}
            <Dialog open={showSaveToBinder} onOpenChange={setShowSaveToBinder}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5 text-primary" />
                            Save to Binder
                        </DialogTitle>
                        <DialogDescription>
                            Save "{resource?.name}" to one of your digital binders for quick reference.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {loadingBinders ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : binders.length === 0 ? (
                            <div className="text-center py-4" data-testid="no-binders-prompt">
                                <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                                <p className="text-sm text-muted-foreground mb-3">
                                    You don't have any binders yet.
                                </p>
                                <Button size="sm" variant="outline" asChild>
                                    <a href="/binder">
                                        <Plus className="h-4 w-4 mr-1" />
                                        Create a Binder
                                    </a>
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label>Select Binder</Label>
                                    <Select value={selectedBinder} onValueChange={setSelectedBinder}>
                                        <SelectTrigger data-testid="binder-select">
                                            <SelectValue placeholder="Choose a binder..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {binders.map(b => (
                                                <SelectItem key={b.binder_id} value={b.binder_id}>
                                                    {b.name}{b.loved_one_name ? ` (${b.loved_one_name})` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes (optional)</Label>
                                    <Textarea
                                        placeholder="Why are you saving this resource?"
                                        rows={2}
                                        value={saveNotes}
                                        onChange={e => setSaveNotes(e.target.value)}
                                        data-testid="save-notes-input"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {binders.length > 0 && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowSaveToBinder(false)}>Cancel</Button>
                            <Button onClick={handleSaveToBinder} disabled={savingToBinder || !selectedBinder} data-testid="confirm-save-to-binder">
                                {savingToBinder ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                                Save to Binder
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
            {/* Claim Organization Dialog */}
            <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-primary" />
                            Claim Organization
                        </DialogTitle>
                        <DialogDescription>
                            Request edit access to "{resource?.name}". An admin will review your claim.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Your Role at This Organization *</Label>
                            <Input
                                placeholder="e.g., Executive Director, Program Manager"
                                value={claimForm.role_at_org}
                                onChange={e => setClaimForm(prev => ({ ...prev, role_at_org: e.target.value }))}
                                data-testid="claim-role-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Why should you have access? (optional)</Label>
                            <Textarea
                                placeholder="Briefly explain your connection to this organization..."
                                rows={3}
                                value={claimForm.justification}
                                onChange={e => setClaimForm(prev => ({ ...prev, justification: e.target.value }))}
                                data-testid="claim-justification-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contact Email</Label>
                            <Input
                                type="email"
                                placeholder="Your organization email"
                                value={claimForm.contact_email}
                                onChange={e => setClaimForm(prev => ({ ...prev, contact_email: e.target.value }))}
                                data-testid="claim-email-input"
                            />
                            <p className="text-xs text-muted-foreground">Admins may reach out to verify your identity.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowClaimDialog(false)}>Cancel</Button>
                        <Button onClick={handleSubmitClaim} disabled={claimLoading} data-testid="submit-claim-btn">
                            {claimLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                            Submit Claim
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ResourceDetailPage;
